<section class="av-card p-4 d-flex flex-column gap-3">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.users.list.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.users.list.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="facade.reload()">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      {{ 'backoffice.common.actions.refresh' | translate }}
    </button>
  </div>

  @if (facade.organizationId()) {
    <div class="org-context d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
      <div class="d-flex flex-column gap-1">
        <span class="av-section-hint">
          {{ 'backoffice.users.list.scoped_to' | translate: { organization: organizationLabel() } }}
        </span>
        <span class="av-meta">{{ 'backoffice.users.root.eligibility_rule' | translate }}</span>
      </div>

      <button type="button" class="av-btn" (click)="clearOrganization()">
        <mat-icon aria-hidden="true">close</mat-icon>
        {{ 'backoffice.users.list.show_all' | translate }}
      </button>
    </div>
  }

  @if (facade.rootIsMissing()) {
    <div class="av-banner p-3 d-flex align-items-start gap-2">
      <mat-icon aria-hidden="true">report_problem</mat-icon>
      <span>{{ 'backoffice.users.list.root_missing_banner' | translate }}</span>
    </div>
  }

  <av-user-filters
    [value]="facade.filters()"
    [disabled]="facade.isMutating()"
    (filtersChange)="applyFilters($event)"
  ></av-user-filters>
</section>

<section class="av-card p-4 mt-3 d-flex flex-column">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.users.list.error.title' | translate"
        [message]="'backoffice.users.list.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.isEmpty()) {
        <av-empty-state
          icon="group_off"
          [title]="'backoffice.users.list.empty.title' | translate"
          [message]="'backoffice.users.list.empty.message' | translate"
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.users.list.columns.user' | translate }}</th>
                <th scope="col">{{ 'backoffice.users.list.columns.organization' | translate }}</th>
                <th scope="col">{{ 'backoffice.users.list.columns.core_role' | translate }}</th>
                <th scope="col">{{ 'backoffice.users.list.columns.status' | translate }}</th>
                <th scope="col">{{ 'backoffice.users.list.columns.licence' | translate }}</th>
                <th scope="col">{{ 'backoffice.users.list.columns.created' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.users.list.columns.actions' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (user of facade.users(); track user.id) {
                <tr>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="cell-strong d-flex align-items-center gap-2">
                        {{ user.fullName }}

                        @if (user.is_root) {
                          <av-status-chip
                            [label]="'backoffice.common.root_chip' | translate"
                            tone="info"
                          ></av-status-chip>
                        }

                        @if (user.isOperator) {
                          <av-status-chip
                            [label]="user.operatorRoleLabelKey | translate"
                            tone="neutral"
                          ></av-status-chip>
                        }
                      </span>
                      <span class="av-meta">{{ user.email || unresolved }}</span>
                    </div>
                  </td>

                  <td class="cell-muted">{{ user.organizationName }}</td>

                  <td>
                    @if (user.ownsCoreSeat) {
                      <av-status-chip
                        [label]="'backoffice.users.list.role_held' | translate"
                        tone="positive"
                      ></av-status-chip>
                    } @else {
                      <av-status-chip
                        [label]="'backoffice.users.list.role_absent' | translate"
                        tone="neutral"
                      ></av-status-chip>
                    }
                  </td>

                  <td class="cell-muted">{{ user.appStatusLabelKey | translate }}</td>

                  <td class="cell-muted">{{ user.licence_state || unresolved }}</td>

                  <td class="cell-muted">
                    {{ user.created_at ? (user.created_at | date: dateFormat) : unresolved }}
                  </td>

                  <td class="text-end">
                    <div class="d-flex align-items-center justify-content-end gap-2">
                      <a
                        class="av-btn"
                        [routerLink]="['/users', user.id]"
                        [matTooltip]="'backoffice.users.list.open_detail' | translate"
                      >
                        <mat-icon aria-hidden="true">open_in_new</mat-icon>
                      </a>

                      @if (facade.canChangeRoot() && !user.is_root) {
                        <button
                          type="button"
                          class="av-btn"
                          [matTooltip]="'backoffice.users.list.make_root' | translate"
                          [disabled]="facade.isMutating()"
                          (click)="openChangeRoot(user)"
                        >
                          <mat-icon aria-hidden="true">military_tech</mat-icon>
                        </button>
                      }

                      @if (facade.canImpersonate() && !user.isOperator) {
                        <button
                          type="button"
                          class="av-btn"
                          [matTooltip]="'backoffice.users.impersonation.scope_note' | translate"
                          [disabled]="facade.isMutating()"
                          (click)="openImpersonate(user)"
                        >
                          <mat-icon aria-hidden="true">switch_account</mat-icon>
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          [disabled]="facade.isMutating()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.users.list.loading.title' | translate"
        [message]="'backoffice.users.list.loading.message' | translate"
      ></av-empty-state>
    }
  }
</section>
